import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "../api";
import type { FeatureList, HostPlan, PanelUser, ProvisionJob, SshKey } from "../types";
import {
  ResourceField,
  fromPackage,
  resourcesFromAccount,
  type AccountResources,
  type PlanResourceSource,
  type ResourceValue
} from "./CreateAccountForm";

export type ModifyAccountPayload = {
  email: string;
  planId: string | null;
  packageId: string | null;
  password?: string;
  sshAccess?: boolean;
  sftpOnly?: boolean;
  denyCron?: boolean;
  createDefaultEmail?: boolean;
  resources?: Record<string, number | null | undefined>;
};

/**
 * Webuzo-style Modify Account — same sectioned design as Add New user.
 */
export function ModifyAccountForm({
  account,
  plans,
  packages,
  sshKeys,
  jobs,
  busy,
  onSubmit,
  onCancel,
  onImpersonate,
  onSuspend,
  onUnsuspend,
  onDelete,
  onProvision,
  onRefreshUsage,
  onAddSshKey,
  onRemoveSshKey,
  currentUserId
}: {
  account: PanelUser;
  plans?: HostPlan[] | PlanResourceSource[];
  packages?: PlanResourceSource[];
  sshKeys: SshKey[];
  jobs: ProvisionJob[];
  busy: boolean;
  currentUserId: string;
  onSubmit: (data: ModifyAccountPayload) => Promise<void>;
  onCancel: () => void;
  onImpersonate?: () => void;
  onSuspend?: () => void;
  onUnsuspend?: () => void;
  onDelete?: () => void;
  onProvision?: () => void;
  onRefreshUsage?: () => void;
  onAddSshKey?: (name: string, publicKey: string) => Promise<void>;
  onRemoveSshKey?: (keyId: string) => Promise<void>;
}) {
  const hostingPlans = useMemo(
    () =>
      (plans && plans.length ? plans : packages || []) as PlanResourceSource[],
    [plans, packages]
  );

  const currentPlanId =
    account.planId || account.packageId || "";

  const [email, setEmail] = useState(account.email || "");
  const [planId, setPlanId] = useState(currentPlanId);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [manual, setManual] = useState(true);
  const [resources, setResources] = useState<AccountResources>(() => {
    const pkg = hostingPlans.find((p) => p.id === currentPlanId);
    return resourcesFromAccount(account, pkg);
  });
  const [sshAccess, setSshAccess] = useState(account.sshAccess);
  const [denyCron, setDenyCron] = useState(Boolean(account.denyCron));
  const [createDefaultEmail, setCreateDefaultEmail] = useState(
    account.createDefaultEmail !== false
  );
  const [featureListId, setFeatureListId] = useState("");
  const [featureLists, setFeatureLists] = useState<FeatureList[]>([]);
  const [keyName, setKeyName] = useState("laptop");
  const [publicKey, setPublicKey] = useState("");
  const [error, setError] = useState("");
  const [localBusy, setLocalBusy] = useState(false);

  // Re-sync when account prop changes (after refresh)
  useEffect(() => {
    const pid = account.planId || account.packageId || "";
    setEmail(account.email || "");
    setPlanId(pid);
    setSshAccess(account.sshAccess);
    setDenyCron(Boolean(account.denyCron));
    setCreateDefaultEmail(account.createDefaultEmail !== false);
    const pkg = hostingPlans.find((p) => p.id === pid);
    setResources(resourcesFromAccount(account, pkg));
  }, [account, hostingPlans]);

  useEffect(() => {
    void api<{ items: FeatureList[] }>("/api/v1/feature-lists")
      .then((r) => {
        setFeatureLists(r.items || []);
        const pid = account.planId || account.packageId || "";
        const fromPlan = hostingPlans.find((p) => p.id === pid);
        if (fromPlan?.featureListId) setFeatureListId(fromPlan.featureListId);
        else {
          const def = r.items?.find((f) => f.isDefault) || r.items?.[0];
          if (def) setFeatureListId(def.id);
        }
      })
      .catch(() => setFeatureLists([]));
  }, [account.planId, account.packageId, hostingPlans]);

  useEffect(() => {
    if (!manual && planId) {
      const p = hostingPlans.find((x) => x.id === planId);
      setResources(fromPackage(p));
    }
  }, [planId, manual, hostingPlans]);

  const setRes = (key: keyof AccountResources, v: ResourceValue) =>
    setResources((r) => ({ ...r, [key]: v }));

  const setupLabel = (status: string | undefined) => {
    switch (status) {
      case "ready":
        return { text: "Active", cls: "ok" };
      case "failed":
        return { text: "Setup failed", cls: "bad" };
      case "provisioning":
        return { text: "Setting up…", cls: "warn" };
      default:
        return { text: status || "—", cls: "warn" };
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (password && password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password && password !== password2) {
      setError("Passwords do not match");
      return;
    }
    setLocalBusy(true);
    try {
      await onSubmit({
        email,
        planId: planId || null,
        packageId: planId || null,
        password: password.length >= 8 ? password : undefined,
        sshAccess,
        sftpOnly: !sshAccess,
        denyCron,
        createDefaultEmail,
        resources: manual
          ? {
              diskMb: resources.diskMb,
              maxInodes: resources.maxInodes,
              bandwidthMb: resources.bandwidthMb,
              maxFtpAccounts: resources.maxFtpAccounts,
              maxEmails: resources.maxEmails,
              maxEmailQuotaMb: resources.maxEmailQuotaMb,
              maxDatabases: resources.maxDatabases,
              maxSubdomains: resources.maxSubdomains,
              maxParkedDomains: resources.maxParkedDomains,
              maxAddonDomains: resources.maxAddonDomains,
              maxDomains: resources.maxDomains,
              maxHourlyEmail: resources.maxHourlyEmail,
              maxFailPercent: resources.maxFailPercent
            }
          : undefined
      });
      setPassword("");
      setPassword2("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLocalBusy(false);
    }
  };

  const isBusy = busy || localBusy;
  const status = setupLabel(account.provisionStatus);

  return (
    <form className="wb-create-form" onSubmit={(e) => void submit(e)}>
      <div className="wb-create-hero">
        <span className="wb-create-hero-icon" aria-hidden>
          ✎
        </span>
        <div>
          <h2>Modify an Account</h2>
          <p className="card-meta" style={{ margin: "4px 0 0" }}>
            <span className="mono">{account.username}</span>
            {account.primaryDomain ? (
              <>
                {" · "}
                <span className="mono">{account.primaryDomain}</span>
              </>
            ) : null}
            {" · "}
            <span className={`badge status-${account.status}`}>
              {account.status}
            </span>{" "}
            <span className={`badge ${status.cls}`}>{status.text}</span>
          </p>
        </div>
        <div className="wb-modify-hero-actions">
          {onImpersonate &&
          account.role === "user" &&
          account.status === "active" &&
          account.provisionStatus === "ready" ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onImpersonate}
            >
              Log in to cPanel
            </button>
          ) : null}
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
            Close
          </button>
        </div>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      {account.provisionError ? (
        <div className="error-banner">
          Setup failed: {account.provisionError}
          {onProvision && account.provisionStatus !== "ready" ? (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => onProvision()}
              >
                Retry setup
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Summary cards */}
      <div className="wb-modify-stats">
        <div className="wb-kpi" style={{ cursor: "default" }}>
          <span className="wb-kpi-icon" style={{ background: "#0EA5E9" }}>
            ◎
          </span>
          <span className="wb-kpi-body">
            <span className="wb-kpi-value" style={{ fontSize: 16 }}>
              {account.homePath || "—"}
            </span>
            <span className="wb-kpi-label">Home path</span>
          </span>
        </div>
        <div className="wb-kpi" style={{ cursor: "default" }}>
          <span className="wb-kpi-icon" style={{ background: "#10B981" }}>
            ⛁
          </span>
          <span className="wb-kpi-body">
            <span className="wb-kpi-value" style={{ fontSize: 20 }}>
              {account.diskUsedMb}
              <span className="faint" style={{ fontSize: 13 }}>
                {" "}
                / {account.quotaDiskMb ?? "∞"} MB
              </span>
            </span>
            <span className="wb-kpi-label">
              Disk used
              {onRefreshUsage && account.provisionStatus === "ready" ? (
                <>
                  {" · "}
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => onRefreshUsage()}
                  >
                    Refresh
                  </button>
                </>
              ) : null}
            </span>
          </span>
        </div>
        <div className="wb-kpi" style={{ cursor: "default" }}>
          <span className="wb-kpi-icon" style={{ background: "#7C3AED" }}>
            👤
          </span>
          <span className="wb-kpi-body">
            <span className="wb-kpi-value" style={{ fontSize: 18 }}>
              {account.ownerUsername || "root"}
            </span>
            <span className="wb-kpi-label">Owner</span>
          </span>
        </div>
      </div>

      {/* Domain Information */}
      <section className="wb-form-section">
        <div className="wb-form-section-hd">Account Information</div>
        <div className="wb-form-section-body">
          <div className="wb-form-grid-3">
            <div className="field">
              <label>Username</label>
              <input value={account.username} disabled className="mono" />
            </div>
            <div className="field">
              <label>Domain</label>
              <input
                value={account.primaryDomain || ""}
                disabled
                className="mono"
                placeholder="(set under Sites)"
              />
            </div>
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          <div className="wb-form-grid-2" style={{ marginTop: 14 }}>
            <div className="field">
              <label>New password (optional)</label>
              <div className="wb-pw-row">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  placeholder="Leave blank to keep current"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="wb-pw-btn"
                  onClick={() => setShowPw((v) => !v)}
                >
                  {showPw ? "🙈" : "👁"}
                </button>
              </div>
            </div>
            <div className="field">
              <label>Re-type new password</label>
              <input
                type={showPw ? "text" : "password"}
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                minLength={8}
                placeholder="Confirm if changing"
                autoComplete="new-password"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Hosting Plan */}
      <section className="wb-form-section">
        <div className="wb-form-section-hd">Hosting Plan</div>
        <div className="wb-form-section-body">
          <div className="wb-form-grid-2">
            <div className="field">
              <label>Choose a Hosting Plan</label>
              <select
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
              >
                <option value="">— — —</option>
                {hostingPlans.map((p) => {
                  const tag =
                    p.planTag === "reseller"
                      ? "Reseller Plan"
                      : p.planTag === "vps"
                        ? "VPS Plan"
                        : p.planTag
                          ? "User Plan"
                          : "";
                  return (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.isDefault ? " (default)" : ""}
                      {tag ? ` [${tag}]` : ""} —{" "}
                      {p.diskMb > 0 ? `${p.diskMb} MB` : "∞ disk"}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="field">
              <label className="wb-check-pill">
                <input
                  type="checkbox"
                  checked={manual}
                  onChange={(e) => setManual(e.target.checked)}
                />
                <span>Select Option Manually</span>
              </label>
              <p className="card-meta" style={{ marginTop: 8 }}>
                Edit resource limits independently of the plan package.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Resource */}
      <section className="wb-form-section">
        <div className="wb-form-section-hd">Resource</div>
        <div className="wb-form-section-body">
          <div className="wb-form-grid-2">
            <ResourceField
              label="Disk Space Quota (MB)"
              value={resources.diskMb}
              disabled={!manual}
              onChange={(v) => setRes("diskMb", v)}
            />
            <ResourceField
              label="Max Inodes"
              value={resources.maxInodes}
              disabled={!manual}
              onChange={(v) => setRes("maxInodes", v)}
            />
            <ResourceField
              label="Monthly Bandwidth Limit (MB)"
              value={resources.bandwidthMb}
              disabled={!manual}
              onChange={(v) => setRes("bandwidthMb", v)}
            />
            <ResourceField
              label="Max FTP Accounts"
              value={resources.maxFtpAccounts}
              disabled={!manual}
              onChange={(v) => setRes("maxFtpAccounts", v)}
            />
            <ResourceField
              label="Max Email Accounts"
              value={resources.maxEmails}
              disabled={!manual}
              onChange={(v) => setRes("maxEmails", v)}
            />
            <ResourceField
              label="Max Quota per Email Address (MB)"
              value={resources.maxEmailQuotaMb}
              disabled={!manual}
              onChange={(v) => setRes("maxEmailQuotaMb", v)}
            />
            <ResourceField
              label="Max SQL Databases"
              value={resources.maxDatabases}
              disabled={!manual}
              onChange={(v) => setRes("maxDatabases", v)}
            />
            <ResourceField
              label="Max Sub Domains"
              value={resources.maxSubdomains}
              disabled={!manual}
              onChange={(v) => setRes("maxSubdomains", v)}
            />
            <ResourceField
              label="Max Parked Domains"
              value={resources.maxParkedDomains}
              disabled={!manual}
              onChange={(v) => setRes("maxParkedDomains", v)}
            />
            <ResourceField
              label="Max Addon Domains"
              value={resources.maxAddonDomains}
              disabled={!manual}
              onChange={(v) => setRes("maxAddonDomains", v)}
            />
            <ResourceField
              label="Max Domains (total)"
              value={resources.maxDomains}
              disabled={!manual}
              onChange={(v) => setRes("maxDomains", v)}
            />
            <ResourceField
              label="Max Hourly Email by Domain Relayed"
              value={resources.maxHourlyEmail}
              disabled={!manual}
              onChange={(v) => setRes("maxHourlyEmail", v)}
            />
          </div>
        </div>
      </section>

      {/* Settings */}
      <section className="wb-form-section">
        <div className="wb-form-section-hd">Settings</div>
        <div className="wb-form-section-body">
          <div className="wb-option-row">
            <label className="wb-check">
              <input
                type="checkbox"
                checked={sshAccess}
                onChange={(e) => setSshAccess(e.target.checked)}
              />
              Shell Access
            </label>
            <label className="wb-check">
              <input
                type="checkbox"
                checked={denyCron}
                onChange={(e) => setDenyCron(e.target.checked)}
              />
              Deny Cron
            </label>
            <label className="wb-check">
              <input
                type="checkbox"
                checked={!createDefaultEmail}
                onChange={(e) => setCreateDefaultEmail(!e.target.checked)}
              />
              Do not create default email account
            </label>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="wb-form-section">
        <div className="wb-form-section-hd">Features</div>
        <div className="wb-form-section-body">
          <div className="field" style={{ maxWidth: 420 }}>
            <label>Features Sets</label>
            <select
              value={featureListId}
              onChange={(e) => setFeatureListId(e.target.value)}
            >
              <option value="">Package / enable all features</option>
              {featureLists.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.isDefault ? " (default)" : ""}
                </option>
              ))}
            </select>
            <p className="card-meta" style={{ marginTop: 8 }}>
              Feature lists are assigned via packages; changing the plan
              updates available cPanel tools.
            </p>
          </div>
        </div>
      </section>

      {/* SSH keys */}
      {account.role === "user" && account.provisionStatus === "ready" ? (
        <section className="wb-form-section">
          <div className="wb-form-section-hd">SSH public keys</div>
          <div className="wb-form-section-body">
            <div className="form-grid">
              <div className="field">
                <label>Name</label>
                <input
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                />
              </div>
              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label>Public key</label>
                <textarea
                  className="textarea"
                  rows={3}
                  value={publicKey}
                  onChange={(e) => setPublicKey(e.target.value)}
                  placeholder="ssh-ed25519 AAAA… comment"
                />
              </div>
            </div>
            {onAddSshKey ? (
              <button
                type="button"
                className="btn btn-primary btn-inline"
                style={{ marginTop: 10 }}
                onClick={async () => {
                  await onAddSshKey(keyName, publicKey);
                  setPublicKey("");
                }}
              >
                Add key
              </button>
            ) : null}
            <div className="table-wrap" style={{ marginTop: 14 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Fingerprint</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sshKeys.map((k) => (
                    <tr key={k.id}>
                      <td>{k.name}</td>
                      <td className="mono faint">{k.fingerprint}</td>
                      <td>
                        {onRemoveSshKey ? (
                          <button
                            type="button"
                            className="link-btn danger"
                            onClick={() => void onRemoveSshKey(k.id)}
                          >
                            Remove
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {sshKeys.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="muted">
                        No SSH keys
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {/* Advanced / danger */}
      <section className="wb-form-section">
        <div className="wb-form-section-hd">Account actions</div>
        <div className="wb-form-section-body">
          <div className="row-actions">
            {account.status === "active" &&
            account.id !== currentUserId &&
            account.role === "user" &&
            onSuspend ? (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onSuspend}
              >
                Suspend
              </button>
            ) : null}
            {account.status === "suspended" && onUnsuspend ? (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onUnsuspend}
              >
                Unsuspend
              </button>
            ) : null}
            {account.id !== currentUserId && onDelete ? (
              <button
                type="button"
                className="btn btn-danger"
                onClick={onDelete}
              >
                Delete Account
              </button>
            ) : null}
          </div>
          {jobs.length > 0 ? (
            <p className="card-meta" style={{ marginTop: 12 }}>
              Last jobs:{" "}
              {jobs
                .slice(0, 3)
                .map((j) => `${j.action || j.id}: ${j.status}`)
                .join(" · ")}
            </p>
          ) : null}
        </div>
      </section>

      <div className="wb-create-actions">
        <button type="submit" className="btn btn-primary" disabled={isBusy}>
          {isBusy ? "Saving…" : "Save User"}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onCancel}
          disabled={isBusy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
